<template>
  <div v-if="activeConversation" class="chat-view">
    <!-- The id names the scrollable history root: it is the search scope of
         the agent screen's find bar. Set here rather than hardcoded inside
         ConversationHistory so other hosts (e.g. AutomationRunToolCall)
         don't duplicate the id. -->
    <ConversationHistory
      id="scrollable-conversation"
      ref="conversationHistoryRef"
      :messages="messages"
      :workspace-id="workspaceId"
      :mentions-enabled="mentionsEnabled"
      :reference-search-organization-id="referenceSearchOrganizationId"
      :reference-context-workspace-id="referenceContextWorkspaceId"
      :integration-organization-ids="integrationOrganizationIds"
      :message-integration-infos="messageIntegrationInfos"
      :message-reference-infos="messageReferenceInfos"
      :in-flight-tool-call-state="inFlightToolCallState"
      :in-flight-assistant-message-index="inFlightAssistantMessageIndex"
    >
      <template #header>
        <div
          v-if="
            configIntegrationInfos.length > 0 || configReferenceInfos.length > 0
          "
          class="message-integration-banner config-context-banner"
        >
          <div
            v-for="info in configIntegrationInfos"
            :key="integrationPillIdentity(info)"
            class="mib-row"
          >
            <img
              v-if="info.logoUri"
              :src="info.logoUri"
              :alt="info.name"
              class="mib-icon"
            />
            <span class="mib-name">{{ info.name }}</span>
            <span class="mib-label"
              >integration added to the context from configuration</span
            >
          </div>
          <div
            v-for="reference in configReferenceInfos"
            :key="reference.id"
            class="mib-row"
          >
            <ReferenceIcon
              :target="{ kind: reference.kind, id: reference.id }"
              class="mib-ref-icon"
            />
            <span class="mib-name">{{ reference.name }}</span>
            <span class="mib-label"
              >{{ referenceKindLabel(reference.kind) }} added to the context
              from configuration</span
            >
          </div>
        </div>
      </template>
      <template #footer>
        <div v-if="isThinking" class="chat-message assistant-message">
          <ConversationToolCalls
            v-if="inProgressToolCalls.length"
            :calls="inProgressToolCalls"
            :tool-call-state="inFlightToolCallState"
          />
          <div class="message-content thinking">
            <div>
              Thinking<span class="thinking-dots"
                ><span>.</span><span>.</span><span>.</span></span
              >
            </div>
          </div>
        </div>
      </template>
    </ConversationHistory>

    <div class="chat-input-wrapper">
      <ChatInput
        ref="chatInput"
        :workspace-id="workspaceId!"
        :reference-search-organization-id="referenceSearchOrganizationId"
        :reference-context-workspace-id="referenceContextWorkspaceId"
        :mentions-enabled="mentionsEnabled"
        :integration-organization-ids="integrationOrganizationIds"
        :attachments="chatAttachments"
        :uploading-files="uploadingFiles"
        :submit-button-state="submitButtonState"
        :error-message="errorMessage"
        @submit="onSubmit"
        @paste="onPaste"
        @file-input-change="onFileInputChange"
        @drop-files="onDropFiles"
        @remove-attachment="removeAttachment"
      >
        <template #placeholder>
          <AgentPlaceholder :mentions-enabled="mentionsEnabled" />
        </template>
      </ChatInput>
      <IntegrationPillsBar
        v-if="workspaceId"
        :workspace-id="workspaceId"
        :context-items="conversationContextItems"
        :is-context-updating="contextUpdating"
        @object-click="onObjectPillClick"
        @integration-click="onIntegrationPillClick"
        @open-context-integration="onOpenContextIntegration"
        @remove-context-integration="onRemoveContextIntegration"
        @remove-context-reference="onRemoveContextReference"
      />
    </div>
  </div>
</template>

<script lang="ts">
import type { Node as ProseMirrorNode } from "prosemirror-model";
import {
  computed,
  defineComponent,
  nextTick,
  ref,
  toRefs,
  watch,
  type PropType,
} from "vue";

import type {
  AiConversation,
  AiPromptContext,
  ConversationId,
  OrgId,
  WorkspaceId,
} from "@ctrl/controller-rpc";

import AgentPlaceholder from "@/components/ai/AgentPlaceholder.vue";
import ChatInput, { type ChatInputHandle } from "@/components/ai/ChatInput.vue";
import { removeConversationContextItem } from "@/components/ai/conversationContext";
import ConversationHistory from "@/components/ai/ConversationHistory.vue";
import ConversationToolCalls from "@/components/ai/ConversationToolCalls.vue";
import {
  clearSendState,
  failSend,
  finishSend,
  getSendState,
  startSend,
  type PendingMessage,
} from "@/components/ai/inFlightSends";
import IntegrationPillsBar, {
  integrationConnectionId,
  integrationPillIdentity,
  type ContextPillItem,
  type IntegrationPill,
  type PillIntegration,
  type ReferenceInfo,
} from "@/components/ai/IntegrationPillsBar.vue";
import {
  createLayoutScrollPin,
  type LayoutScrollPin,
} from "@/components/ai/layoutScrollPin";
import { pendingMessage } from "@/components/ai/pendingMessage";
import type { AgentChatSession } from "@/components/ai/useAgent";
import {
  useAttachments,
  type AiAttachment,
} from "@/components/ai/useAttachments";
import { extractContext } from "@/components/editor/context";
import { parseMarkupToDoc } from "@/components/editor/serialize";
import { displayTitle } from "@/components/graph/title";
import ReferenceIcon from "@/components/icons/ReferenceIcon.vue";
import * as inject from "@/components/inject";
import {
  isMcpIntegrationKey,
  mcpPackageIdFromIntegrationKey,
} from "@/components/integrations/mcpCandidates";
import { resolveIntegrationOrganizationIds } from "@/components/integrations/useIntegrations";
import { referenceKindLabel } from "@/references/kinds";
import { resolveReferenceTitle } from "@/references/resolve";
import { RouteNames } from "@/routes/types";
import { navigateTo } from "@/utils/navigate";

type ReferenceCandidate = Omit<ReferenceInfo, "name"> & {
  name?: string;
};

export default defineComponent({
  name: "ConversationView",
  components: {
    AgentPlaceholder,
    ChatInput,
    ConversationHistory,
    ConversationToolCalls,
    IntegrationPillsBar,
    ReferenceIcon,
  },
  props: {
    session: { type: Object as PropType<AgentChatSession>, required: true },
    conversationId: {
      type: String as PropType<ConversationId>,
      required: true,
    },
    activeConversation: {
      type: Object as PropType<AiConversation | null>,
      required: true,
    },
    workspaceId: {
      type: String as PropType<WorkspaceId | undefined>,
      default: undefined,
    },
    organizationId: { type: String as PropType<OrgId>, required: true },
    agentConfigContext: {
      type: Object as PropType<AiPromptContext>,
      default: () => ({}),
    },
    mentionsEnabled: { type: Boolean, default: false },
    referenceSearchOrganizationId: {
      type: String as PropType<OrgId | null>,
      default: null,
    },
    referenceContextWorkspaceId: {
      type: String as PropType<WorkspaceId | null>,
      default: null,
    },
    integrationOrganizationIds: {
      type: Array as PropType<OrgId[] | undefined>,
      default: undefined,
    },
  },
  emits: {
    "integration-pill-click": (_pill: IntegrationPill) => true,
  },
  async setup(props, { emit }) {
    const {
      organizationId,
      conversationId,
      workspaceId,
      integrationOrganizationIds,
    } = toRefs(props);

    const controller = inject.controller();
    const logger = inject.logger("ConversationView");
    const router = inject.router();
    const integrations = inject.integrations();
    const baseLogger = logger.base();

    const chatInput = ref<ChatInputHandle | null>(null);
    const conversationHistoryRef = ref<InstanceType<
      typeof ConversationHistory
    > | null>(null);
    let layoutScrollPin: LayoutScrollPin | null = null;
    const session = props.session;

    const scrollChatToBottom = async (smooth = true) => {
      await nextTick();
      const el = conversationHistoryRef.value?.historyEl;
      if (!el) return;
      el.scrollTo({
        top: el.scrollHeight,
        behavior: smooth ? "smooth" : "auto",
      });
    };

    watch(
      conversationHistoryRef,
      (history, _previousHistory, onCleanup) => {
        const el = history?.historyEl;
        if (!el) return;

        const scrollPin = createLayoutScrollPin(el);
        layoutScrollPin = scrollPin;
        onCleanup(() => {
          scrollPin.release();
          if (layoutScrollPin === scrollPin) layoutScrollPin = null;
        });
      },
      { flush: "post" }
    );

    const {
      attachments: chatAttachments,
      uploading: uploadingFiles,
      addFiles,
      remove: removeAttachment,
      clear: clearAttachments,
    } = useAttachments(controller, workspaceId);

    const onDropFiles = (files: File[]) => {
      if (files.length > 0) void addFiles(files);
    };

    const onFileInputChange = (event: Event) => {
      if (!(event.target instanceof HTMLInputElement)) return;
      const input = event.target;
      if (input.files && input.files.length > 0) {
        void addFiles(Array.from(input.files));
        input.value = "";
      }
    };

    const onPaste = (event: ClipboardEvent) => {
      const items = event.clipboardData?.items;
      if (!items) return;
      const files: File[] = [];
      for (let i = 0; i < items.length; i++) {
        if (items[i].kind === "file") {
          const file = items[i].getAsFile();
          if (file) files.push(file);
        }
      }
      if (files.length > 0) {
        event.preventDefault();
        void addFiles(files);
      }
    };

    // Per-conversation in-flight send state lives in a module-level store
    // (see ./inFlightSends.ts) so it survives unmount/remount. Without this,
    // navigating away while the agent is "thinking" and coming back would lose
    // the optimistic user message and thinking indicator, making the page
    // appear blank until the server history catches up.
    const sendState = computed(() => getSendState(conversationId.value));
    const inProgressToolCalls = computed(
      () => props.activeConversation?.in_progress?.tool_calls ?? []
    );
    const isThinking = computed(
      () =>
        sendState.value?.isSending === true ||
        props.activeConversation?.in_progress != null
    );
    const inFlightToolCallState = computed(
      () => sendState.value?.toolCallState
    );
    const inFlightAssistantMessageIndex = computed(() => {
      const state = sendState.value;
      return state ? state.historyLengthAtStart + 1 : -1;
    });
    const errorMessage = computed(() => sendState.value?.errorMessage ?? null);

    // Displayed messages: server history merged with any pending optimistic
    // user message and the most recent citations (which the server history may
    // not yet reflect right after a send resolves).
    const messages = computed<PendingMessage[]>(() => {
      const history = props.activeConversation?.history ?? [];
      const state = sendState.value;
      if (!state) return history;
      const reconciledPendingCount = Math.max(
        0,
        history.length - state.historyLengthAtStart
      );
      return [
        ...history,
        ...state.pendingMessages.slice(reconciledPendingCount),
      ];
    });

    // Scroll the chat history to the bottom when a conversation opens (new
    // mount or switching conversations) and whenever messages are appended or
    // the thinking indicator appears. Initial opens use an instant scroll so
    // the user never sees the scroll animate on arrival; subsequent updates
    // use smooth scrolling for a nicer feel.
    watch(
      () => props.activeConversation?.id,
      (id) => {
        if (!id) return;
        layoutScrollPin?.pin();
        void scrollChatToBottom(false);
      },
      { immediate: true, flush: "post" }
    );
    watch(
      () => messages.value.length,
      (newLen, oldLen) => {
        if (newLen <= (oldLen ?? 0)) return;
        layoutScrollPin?.pin();
        void scrollChatToBottom();
      },
      { flush: "post" }
    );
    watch(
      isThinking,
      (thinking) => {
        if (!thinking) return;
        layoutScrollPin?.pin();
        void scrollChatToBottom();
      },
      { flush: "post" }
    );

    watch(
      [() => props.activeConversation?.history.length, sendState],
      () => {
        const cid = conversationId.value;
        const state = sendState.value;
        const historyLength = props.activeConversation?.history.length ?? 0;
        if (!cid || !state || state.isSending || state.errorMessage) return;
        if (state.pendingMessages.length === 0) return;
        if (
          historyLength >=
          state.historyLengthAtStart + state.pendingMessages.length
        ) {
          clearSendState(cid);
        }
      },
      { flush: "post" }
    );

    // Shared send path: register the optimistic user message + thinking state
    // in the module store, run the send, then reconcile on resolution.
    const doSend = async (
      doc: ProseMirrorNode,
      userText: string,
      attachments?: AiAttachment[]
    ) => {
      const cid = conversationId.value;
      if (!cid) return;
      startSend(
        cid,
        {
          role: "user" as const,
          content: userText,
          ...(attachments && attachments.length > 0
            ? {
                attachments: attachments.map((a) => ({
                  created: new Date(),
                  id: a.id,
                  mime: a.mime,
                  name: a.name,
                  url: a.url,
                })),
              }
            : {}),
        },
        props.activeConversation?.history.length ?? 0
      );
      try {
        const result = await session.send(doc, cid, attachments);
        finishSend(cid, result.document, result.citations ?? []);
      } catch (err) {
        failSend(
          cid,
          err instanceof Error ? err.message : "Unable to reach AI model."
        );
      }
    };

    const submitButtonState = computed(() =>
      isThinking.value ? "loading" : "default"
    );

    const onSubmit = async (proseMirrorDoc: ProseMirrorNode) => {
      if (!proseMirrorDoc || isThinking.value) return;
      const { serializeDoc } = await import("@/components/editor/serialize");
      const text = serializeDoc(proseMirrorDoc);

      const attachments =
        chatAttachments.value.length > 0
          ? chatAttachments.value.map(({ id, name, url, mime }) => ({
              id,
              name,
              url,
              mime,
            }))
          : undefined;
      clearAttachments();

      chatInput.value?.setValue?.("");
      await doSend(proseMirrorDoc, text, attachments);
    };

    const focusChatComposer = async () => {
      await nextTick();
      chatInput.value?.focusEditor?.();
      requestAnimationFrame(() => {
        chatInput.value?.focusEditor?.();
      });
    };

    watch(
      [conversationId, () => chatInput.value],
      () => {
        const incoming = pendingMessage.value;
        if (incoming && incoming.conversationId === conversationId.value) {
          if (incoming.sendImmediately === false) {
            if (chatInput.value) {
              pendingMessage.value = null;
              chatInput.value.setValue(incoming.text);
            }
          } else {
            pendingMessage.value = null;
            const incomingAttachments = incoming.attachments?.map(
              ({ id, name, url, mime }) => ({ id, name, url, mime })
            );
            void doSend(incoming.doc, incoming.text, incomingAttachments);
          }
        }
        void focusChatComposer();
      },
      { immediate: true, flush: "post" }
    );

    const conversationContext = ref<AiPromptContext>({});
    const contextUpdating = ref(false);
    let syncedConversationId: ConversationId | null = null;
    watch(
      () =>
        [
          props.activeConversation?.id ?? null,
          props.activeConversation?.context,
        ] as const,
      ([id, context]) => {
        const conversationChanged = id !== syncedConversationId;
        syncedConversationId = id;
        if (!conversationChanged && contextUpdating.value) return;
        conversationContext.value = context ?? {};
      },
      { immediate: true, deep: true }
    );

    const candidateOrganizationIds = computed<readonly OrgId[] | undefined>(
      () =>
        resolveIntegrationOrganizationIds(
          integrationOrganizationIds.value,
          organizationId.value
        )
    );
    const integrationCandidateData = integrations.candidatesFor(
      candidateOrganizationIds
    );

    // Parse each user message once, extracting both integrations and references
    const messageContexts = computed(() =>
      messages.value.map((msg) => {
        if (msg.role !== "user")
          return {
            integrations: Array<{
              integrationKey: string;
              connectionId: string;
            }>(),
            references: Array<ReferenceCandidate>(),
          };
        const doc = parseMarkupToDoc(msg.content, baseLogger);
        const context = extractContext(
          doc,
          ["integrations", "references"],
          baseLogger
        );
        return {
          integrations: context.integrations.map(({ key, connectionId }) => ({
            integrationKey: key,
            connectionId,
          })),
          references: context.references,
        };
      })
    );

    // Live-fetched titles keyed by reference id
    const fetchedReferenceTitles = ref<Record<string, string>>({});
    // Track references whose targets no longer exist
    const notFoundReferenceIds = ref<Record<string, true>>({});
    // Track in-flight fetches to avoid duplicate concurrent requests
    const fetchingIds = new Set<string>();

    const fetchReferenceTitle = async (
      reference: ReferenceCandidate
    ): Promise<void> => {
      if (
        fetchedReferenceTitles.value[reference.id] ||
        notFoundReferenceIds.value[reference.id] ||
        fetchingIds.has(reference.id)
      )
        return;
      fetchingIds.add(reference.id);
      try {
        const resolved = await resolveReferenceTitle(controller, {
          kind: reference.kind,
          id: reference.id,
        });
        if (!resolved.exists) {
          logger.warn(
            "reference target not found: hiding it from the context banner and pills",
            { kind: reference.kind, id: reference.id, name: reference.name }
          );
          notFoundReferenceIds.value[reference.id] = true;
        } else {
          fetchedReferenceTitles.value[reference.id] = displayTitle(
            resolved.title ?? reference.name
          );
        }
      } catch (error) {
        logger.warn(
          "reference title resolution failed: falling back to the stored name",
          {
            kind: reference.kind,
            id: reference.id,
            name: reference.name,
            error,
          }
        );
        fetchedReferenceTitles.value[reference.id] = displayTitle(
          reference.name
        );
      } finally {
        fetchingIds.delete(reference.id);
      }
    };

    const getResolvedReference = (
      reference: ReferenceCandidate
    ): ReferenceInfo | null => {
      const name = fetchedReferenceTitles.value[reference.id];
      return name ? { ...reference, name } : null;
    };

    const resolveIntegrationInfo = (
      key: string,
      connectionId?: string
    ): IntegrationPill => {
      const candidate = connectionId
        ? integrationCandidateData.candidates.value.find(
            (item) => item.connectionId === connectionId
          )
        : integrationCandidateData.candidates.value.find(
            (item) => item.integrationKey === key
          );
      const meta = integrationCandidateData.lookup.getMetaByKey(key);
      const membraneCandidate =
        candidate?.kind === "membrane" ? candidate : undefined;
      const membraneMeta = meta?.kind === "membrane" ? meta : undefined;
      const integration: PillIntegration = isMcpIntegrationKey(key)
        ? {
            kind: "mcp",
            integrationKey: key,
            serverId: connectionId,
          }
        : {
            kind: "membrane",
            integrationKey: key,
            connectionId,
            appId: membraneCandidate?.appId ?? membraneMeta?.appId,
            organizationId:
              membraneCandidate?.organizationId ?? membraneMeta?.organizationId,
          };
      return {
        name: candidate?.name ?? meta?.name ?? key,
        logoUri: candidate?.logoUri ?? meta?.logoUri,
        integration,
      };
    };

    // References from agent instructions / user preferences (deduplicated by id)
    const configRawReferences = computed<ReferenceCandidate[]>(() => {
      const seen = new Set<string>();
      const references: ReferenceCandidate[] = [];
      for (const ref of props.agentConfigContext.references ?? []) {
        if (seen.has(ref.data.id)) continue;
        seen.add(ref.data.id);
        references.push({
          id: ref.data.id,
          name: ref.key,
          kind: ref.data.kind,
        });
      }
      return references;
    });

    // Config integrations as pills (deduped by connection)
    const configIntegrationInfos = computed<IntegrationPill[]>(() => {
      const seen = new Set<string>();
      return (props.agentConfigContext.integrations ?? [])
        .filter(({ key, data }) => {
          const identity = `${key}:${data}`;
          if (seen.has(identity)) return false;
          seen.add(identity);
          return true;
        })
        .map(({ key, data }) => resolveIntegrationInfo(key, data));
    });

    // Config references resolved with live titles, excluding deleted targets
    const configReferenceInfos = computed<ReferenceInfo[]>(() =>
      configRawReferences.value.flatMap((reference) => {
        if (notFoundReferenceIds.value[reference.id]) return [];
        const resolved = getResolvedReference(reference);
        return resolved ? [resolved] : [];
      })
    );
    const conversationContextReferences = computed<ReferenceCandidate[]>(() =>
      (conversationContext.value.references ?? []).map(({ key, data }) => ({
        id: data.id,
        name: key,
        kind: data.kind,
      }))
    );

    // Most recently accumulated conversation items come first. Configured
    // items are immutable from a conversation and therefore come last.
    const conversationIntegrationInfos = computed<IntegrationPill[]>(() => {
      const configured = new Set(
        configIntegrationInfos.value.map(integrationPillIdentity)
      );
      const seen = new Set<string>();
      const pills: IntegrationPill[] = [];
      for (const integration of [
        ...(conversationContext.value.integrations ?? []),
      ].reverse()) {
        const pill = {
          ...resolveIntegrationInfo(integration.key, integration.data),
          removable: true,
        };
        const identity = integrationPillIdentity(pill);
        if (configured.has(identity) || seen.has(identity)) continue;
        seen.add(identity);
        pills.push(pill);
      }
      return pills;
    });

    // The context persisted by the controller is authoritative. Historical
    // message parsing below is only used for per-message attribution.
    const conversationReferenceInfos = computed<ReferenceInfo[]>(() => {
      const configured = new Set(
        configReferenceInfos.value.map(({ id }) => id)
      );
      const seen = new Set<string>();
      const references: ReferenceInfo[] = [];
      for (const reference of [
        ...conversationContextReferences.value,
      ].reverse()) {
        if (
          configured.has(reference.id) ||
          seen.has(reference.id) ||
          notFoundReferenceIds.value[reference.id]
        )
          continue;
        const resolved = getResolvedReference(reference);
        if (!resolved) continue;
        seen.add(reference.id);
        references.push({ ...resolved, removable: true });
      }
      return references;
    });

    const conversationContextItems = computed<ContextPillItem[]>(() => {
      const integrationItems = new Map(
        conversationIntegrationInfos.value.map((pill) => [
          integrationPillIdentity(pill),
          { ...pill, type: "integration" } satisfies ContextPillItem,
        ])
      );
      const referenceItems = new Map(
        conversationReferenceInfos.value.map((reference) => [
          reference.id,
          { ...reference, type: "reference" } satisfies ContextPillItem,
        ])
      );
      const items: ContextPillItem[] = [];

      // History supplies cross-type recency, while the persisted context above
      // remains authoritative about which items are currently active.
      for (const context of [...messageContexts.value].reverse()) {
        for (const integration of [...context.integrations].reverse()) {
          const identity = `${integration.integrationKey}:${integration.connectionId}`;
          const item = integrationItems.get(identity);
          if (!item) continue;
          items.push(item);
          integrationItems.delete(identity);
        }
        for (const reference of [...context.references].reverse()) {
          const item = referenceItems.get(reference.id);
          if (!item) continue;
          items.push(item);
          referenceItems.delete(reference.id);
        }
      }

      items.push(...integrationItems.values(), ...referenceItems.values());
      items.push(
        ...configIntegrationInfos.value.map(
          (pill): ContextPillItem => ({ ...pill, type: "integration" })
        ),
        ...configReferenceInfos.value.map(
          (reference): ContextPillItem => ({
            ...reference,
            type: "reference",
          })
        )
      );
      return items;
    });

    // Per-message resolved infos
    const messageIntegrationInfos = computed<IntegrationPill[][]>(() =>
      messageContexts.value.map((ctx) =>
        ctx.integrations.map(({ integrationKey, connectionId }) =>
          resolveIntegrationInfo(integrationKey, connectionId)
        )
      )
    );

    const messageReferenceInfos = computed<ReferenceInfo[][]>(() =>
      messageContexts.value.map((ctx) =>
        ctx.references.flatMap((reference) => {
          if (notFoundReferenceIds.value[reference.id]) return [];
          const resolved = getResolvedReference(reference);
          return resolved ? [resolved] : [];
        })
      )
    );

    watch(
      [messageContexts, configRawReferences, conversationContextReferences],
      ([contexts, configReferences, conversationReferences]) => {
        const candidates = [
          ...contexts.flatMap((ctx) => ctx.references),
          ...configReferences,
          ...conversationReferences,
        ].filter(
          (reference) =>
            !fetchedReferenceTitles.value[reference.id] &&
            !notFoundReferenceIds.value[reference.id] &&
            !fetchingIds.has(reference.id)
        );
        const unique = [
          ...new Map(
            candidates.map((reference) => [reference.id, reference])
          ).values(),
        ];
        void Promise.all(unique.map(fetchReferenceTitle));
      },
      { immediate: true }
    );

    const onIntegrationPillClick = (pill: IntegrationPill) => {
      chatInput.value?.insertIntegration?.(
        pill.integration.integrationKey,
        integrationConnectionId(pill.integration)
      );
      emit("integration-pill-click", pill);
    };

    const onObjectPillClick = () => {
      chatInput.value?.linkObject?.();
    };

    const onOpenContextIntegration = (pill: IntegrationPill) => {
      if (pill.integration.kind === "mcp") {
        const packageId = mcpPackageIdFromIntegrationKey(
          pill.integration.integrationKey
        );
        if (packageId) {
          void navigateTo(RouteNames.SettingsMcpIntegration, router, {
            params: { orgId: organizationId.value, packageId },
          });
        } else {
          void navigateTo(RouteNames.SettingsIntegrations, router, {
            params: { orgId: organizationId.value },
          });
        }
        return;
      }

      const appId = pill.integration.appId;
      const preferredOrganizationId = pill.integration.organizationId;
      const app = appId
        ? integrationCandidateData.getServiceById(
            appId,
            preferredOrganizationId
          )
        : undefined;
      const appOrganizationId = appId
        ? integrationCandidateData.getOrganizationIdByServiceId(
            appId,
            preferredOrganizationId
          )
        : undefined;
      const targetOrganizationId =
        appOrganizationId ?? preferredOrganizationId ?? organizationId.value;

      if (!app?.key || !targetOrganizationId) {
        void navigateTo(RouteNames.SettingsIntegrations, router, {
          params: { orgId: targetOrganizationId ?? organizationId.value },
        });
        return;
      }

      void navigateTo(RouteNames.SettingsMembraneIntegration, router, {
        params: { orgId: targetOrganizationId, appKey: app.key },
      });
    };

    const updateConversationContext = async (nextContext: AiPromptContext) => {
      if (contextUpdating.value) return;
      const id = conversationId.value;
      const previousContext = conversationContext.value;
      contextUpdating.value = true;
      conversationContext.value = nextContext;
      try {
        await controller.ai.agent.conversation.updateContext(
          organizationId.value,
          id,
          nextContext
        );
      } catch (err) {
        logger.error("Failed to update conversation context", err);
        if (conversationId.value === id) {
          conversationContext.value = previousContext;
          controller.notifications.notify(
            "Unable to update the conversation context."
          );
        }
      } finally {
        contextUpdating.value = false;
      }
    };

    const onRemoveContextIntegration = (pill: IntegrationPill) => {
      const item = conversationContext.value.integrations?.find(
        ({ key, data }) =>
          key === pill.integration.integrationKey &&
          data === integrationConnectionId(pill.integration)
      );
      if (!item) return;
      void updateConversationContext(
        removeConversationContextItem(conversationContext.value, {
          type: "integration",
          item,
        })
      );
    };

    const onRemoveContextReference = (reference: ReferenceInfo) => {
      const item = conversationContext.value.references?.find(
        ({ data }) => data.kind === reference.kind && data.id === reference.id
      );
      if (!item) return;
      void updateConversationContext(
        removeConversationContextItem(conversationContext.value, {
          type: "reference",
          item,
        })
      );
    };

    return {
      integrationPillIdentity,
      messages,
      chatInput,
      conversationHistoryRef,
      chatAttachments,
      uploadingFiles,
      removeAttachment,
      onPaste,
      onFileInputChange,
      isThinking,
      inProgressToolCalls,
      inFlightToolCallState,
      inFlightAssistantMessageIndex,
      errorMessage,
      submitButtonState,
      onSubmit,
      onDropFiles,
      contextUpdating,
      configIntegrationInfos,
      configReferenceInfos,
      conversationContextItems,
      messageIntegrationInfos,
      messageReferenceInfos,
      onObjectPillClick,
      onIntegrationPillClick,
      onOpenContextIntegration,
      onRemoveContextIntegration,
      onRemoveContextReference,
      referenceKindLabel,
    };
  },
});
</script>

<style scoped lang="scss">
@use "../../styles/agent" as *;

.chat-view {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  .chat-input-wrapper {
    @include agent-content-column;
    padding-top: 16px;
    padding-bottom: 24px;
  }
}

.message-integration-banner {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-bottom: 6px;

  .mib-row {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .mib-icon {
    width: 14px;
    height: 14px;
    object-fit: contain;
    border-radius: $border-radius-s;
    flex-shrink: 0;
  }

  .mib-ref-icon {
    width: 14px;
    height: 14px;
    flex-shrink: 0;

    :deep(.fill) {
      @include themed() {
        fill: t($grey-50);
      }
    }
  }

  .mib-obj-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 14px;
    height: 14px;
    flex-shrink: 0;
    font-size: 12px;
    line-height: 1;
    @include themed() {
      color: t($grey-50);
    }
  }

  .mib-name {
    @extend %font-caption-medium;
    @include themed() {
      color: t($primary);
    }
  }

  .mib-label {
    @extend %font-caption-regular;
    @include themed() {
      color: t($grey-40);
    }
  }
}

.config-context-banner {
  margin-bottom: 0;
  align-items: flex-end;

  .mib-row {
    display: flex;
    justify-content: flex-end;
    align-items: center;
  }

  .mib-name {
    margin-inline-start: 4px;
  }
}

.thinking {
  @extend %font-body-regular;
  @include themed() {
    color: t($grey-40);
  }
}

.thinking-dots span {
  animation: thinking-blink 1.4s infinite;

  &:nth-child(2) {
    animation-delay: 0.2s;
  }

  &:nth-child(3) {
    animation-delay: 0.4s;
  }
}

@keyframes thinking-blink {
  0%,
  80%,
  100% {
    opacity: 0;
  }

  40% {
    opacity: 1;
  }
}
</style>
